import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { 
  Ticket, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Mail, 
  Phone,
  Loader2,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const GuideApplicationStatusPage = () => {
  const { applicationId } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [debugCode, setDebugCode] = useState(null);
  
  const loadStatus = async () => {
    try {
      const response = await api.get(`/guide-apply/${applicationId}/status`);
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load application status');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadStatus();
    // Poll for updates every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [applicationId]);
  
  const sendPhoneCode = async () => {
    setSendingCode(true);
    setDebugCode(null);
    try {
      const response = await api.post('/guide-apply/send-phone-code', {
        application_id: applicationId
      });
      toast.success(response.data.message);
      if (response.data.debug_code) {
        setDebugCode(response.data.debug_code);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };
  
  const verifyPhoneCode = async () => {
    if (phoneCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    try {
      await api.post('/guide-apply/verify-phone', {
        application_id: applicationId,
        code: phoneCode
      });
      toast.success('Phone verified successfully!');
      loadStatus();
      setPhoneCode('');
      setDebugCode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifyingCode(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (!status) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Application Not Found</h2>
            <p className="text-slate-600 mb-4">The application ID may be incorrect or expired.</p>
            <Link to="/guide/join">
              <Button>Submit New Application</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const getStatusBadge = () => {
    switch (status.status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">Pending Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge>{status.status}</Badge>;
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/guide/join">
            <Button variant="ghost" className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Ticket className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">Muoov</span>
          </Link>
        </div>
        
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {status.status === 'pending' && <Clock className="w-8 h-8 text-indigo-600" />}
              {status.status === 'approved' && <CheckCircle className="w-8 h-8 text-green-600" />}
              {status.status === 'rejected' && <XCircle className="w-8 h-8 text-red-600" />}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Application Status</CardTitle>
            <CardDescription>
              Application ID: {applicationId.slice(0, 8)}...
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status */}
            <div className="text-center">
              {getStatusBadge()}
            </div>
            
            {status.status === 'rejected' && status.rejection_reason && (
              <Alert className="bg-red-50 border-red-200">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>Reason:</strong> {status.rejection_reason}
                </AlertDescription>
              </Alert>
            )}
            
            {status.status === 'approved' && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Congratulations! Your application has been approved. Check your email for login credentials.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Verification Steps */}
            {status.status === 'pending' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Verification Steps</h3>
                
                {/* Email Verification */}
                <div className={`flex items-center gap-3 p-4 rounded-lg ${
                  status.email_verified ? 'bg-green-50' : 'bg-amber-50'
                }`}>
                  {status.email_verified ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <Mail className="w-6 h-6 text-amber-600" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${status.email_verified ? 'text-green-800' : 'text-amber-800'}`}>
                      Email Verification
                    </p>
                    <p className={`text-sm ${status.email_verified ? 'text-green-600' : 'text-amber-600'}`}>
                      {status.email_verified 
                        ? 'Email verified successfully' 
                        : 'Check your inbox for the verification link'}
                    </p>
                  </div>
                  {status.email_verified && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>
                
                {/* Phone Verification */}
                <div className={`p-4 rounded-lg ${
                  status.phone_verified ? 'bg-green-50' : 'bg-slate-50'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    {status.phone_verified ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <Phone className="w-6 h-6 text-slate-400" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${status.phone_verified ? 'text-green-800' : 'text-slate-800'}`}>
                        Phone Verification
                      </p>
                      <p className={`text-sm ${status.phone_verified ? 'text-green-600' : 'text-slate-500'}`}>
                        {status.phone_verified 
                          ? 'Phone verified successfully' 
                          : 'Verify your phone number via SMS'}
                      </p>
                    </div>
                    {status.phone_verified && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  
                  {!status.phone_verified && status.email_verified && (
                    <div className="space-y-3 mt-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={sendPhoneCode}
                        disabled={sendingCode}
                        data-testid="send-code-btn"
                      >
                        {sendingCode ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending Code...
                          </>
                        ) : (
                          <>
                            <Phone className="w-4 h-4 mr-2" />
                            Send Verification Code
                          </>
                        )}
                      </Button>
                      
                      {debugCode && (
                        <Alert className="bg-amber-50 border-amber-200">
                          <AlertDescription className="text-amber-700 text-sm">
                            <strong>Debug:</strong> SMS unavailable. Use code: <code className="bg-amber-100 px-2 py-1 rounded">{debugCode}</code>
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter 6-digit code"
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                          className="text-center text-lg tracking-widest"
                          data-testid="phone-code-input"
                        />
                        <Button
                          onClick={verifyPhoneCode}
                          disabled={verifyingCode || phoneCode.length !== 6}
                          data-testid="verify-code-btn"
                        >
                          {verifyingCode ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Verify'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!status.phone_verified && !status.email_verified && (
                    <p className="text-sm text-slate-500 mt-2">
                      Please verify your email first before phone verification.
                    </p>
                  )}
                </div>
                
                {/* Progress */}
                <div className="pt-4">
                  <div className="flex justify-between text-sm text-slate-600 mb-2">
                    <span>Verification Progress</span>
                    <span>{(status.email_verified ? 1 : 0) + (status.phone_verified ? 1 : 0)}/2</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${((status.email_verified ? 1 : 0) + (status.phone_verified ? 1 : 0)) * 50}%` }}
                    />
                  </div>
                </div>
                
                {status.email_verified && status.phone_verified && (
                  <Alert className="bg-indigo-50 border-indigo-200">
                    <CheckCircle className="h-4 w-4 text-indigo-600" />
                    <AlertDescription className="text-indigo-700">
                      All verifications complete! Our team will review your application shortly.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            
            {/* Refresh Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setLoading(true); loadStatus(); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Status
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GuideApplicationStatusPage;
